<template>
  <div class="login-form-container">
    <!-- 二维码登录 -->
    <div v-if="loginMethod === 'qr'">
      <QRLoginForm :on-success="handleLoginSuccess" />

      <!-- 登录方式切换按钮 -->
      <div class="login-method-switch">
        <Button
          icon="pi pi-mobile"
          :title="$t('auth.login.smsLogin')"
          severity="secondary"
          text
          rounded
          class="switch-button"
          @click="switchLoginMethod('sms')"
        />
        <Button
          icon="pi pi-key"
          :title="$t('auth.login.passwordLogin')"
          severity="secondary"
          text
          rounded
          class="switch-button"
          @click="switchLoginMethod('password')"
        />
      </div>
    </div>

    <!-- 密码登录表单 -->
    <form
      v-else-if="loginMethod === 'password'"
      class="login-form"
      @submit.prevent="handleLogin"
    >
      <div class="form-group">
        <label for="username">{{ $t('auth.login.usernameLabel') }}</label>
        <InputText
          id="username"
          v-model="form.username"
          type="text"
          :placeholder="$t('auth.login.usernamePlaceholder')"
          :class="{ 'p-invalid': errors.username }"
          required
        />
        <small v-if="errors.username" class="error-message">{{
          errors.username
        }}</small>
      </div>

      <div class="form-group">
        <label for="password">{{ $t('auth.login.passwordLabel') }}</label>
        <div class="password-input-container">
          <InputText
            id="password"
            v-model="form.password"
            :type="showPassword ? 'text' : 'password'"
            :placeholder="$t('auth.login.passwordPlaceholder')"
            :class="{ 'p-invalid': errors.password }"
            required
          />
          <Button
            :icon="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"
            class="password-toggle"
            severity="secondary"
            text
            @click="togglePassword"
          />
        </div>
        <small v-if="errors.password" class="error-message">{{
          errors.password
        }}</small>
      </div>

      <div v-if="authStore.error" class="error-message global-error">
        {{ authStore.error }}
      </div>

      <div class="form-actions">
        <Button
          type="submit"
          :label="$t('auth.login.loginButton')"
          icon="pi pi-sign-in"
          :loading="authStore.isLoading"
          class="login-button"
        />
      </div>

      <!-- 登录方式切换按钮 -->
      <div class="login-method-switch">
        <Button
          icon="pi pi-qrcode"
          title="扫码登录"
          severity="secondary"
          text
          rounded
          class="switch-button"
          @click="switchLoginMethod('qr')"
        />
        <Button
          icon="pi pi-mobile"
          :title="$t('auth.login.smsLogin')"
          severity="secondary"
          text
          rounded
          class="switch-button"
          @click="switchLoginMethod('sms')"
        />
      </div>
    </form>

    <!-- 短信登录表单 -->
    <div v-else>
      <SmsLoginForm
        :on-success="handleLoginSuccess"
        @switch-to-password="switchLoginMethod('password')"
      />

      <!-- 登录方式切换按钮 -->
      <div class="login-method-switch">
        <Button
          icon="pi pi-qrcode"
          title="扫码登录"
          severity="secondary"
          text
          rounded
          class="switch-button"
          @click="switchLoginMethod('qr')"
        />
        <Button
          icon="pi pi-key"
          :title="$t('auth.login.passwordLogin')"
          severity="secondary"
          text
          rounded
          class="switch-button"
          @click="switchLoginMethod('password')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { onMounted, reactive, ref } from 'vue'

import { useAuthStore } from '@/stores/authStore'

import QRLoginForm from './QRLoginForm.vue'
import SmsLoginForm from './SmsLoginForm.vue'

interface Props {
  showRegisterLink?: boolean
  onSuccess?: () => void
}

interface Emits {
  (e: 'login-method-change', method: 'password' | 'sms' | 'qr'): void
}

const props = withDefaults(defineProps<Props>(), {
  showRegisterLink: true
})

const emit = defineEmits<Emits>()

const authStore = useAuthStore()
const showPassword = ref(false)
const loginMethod = ref<'password' | 'sms' | 'qr'>('qr')

const form = reactive({
  username: '',
  password: ''
})

const errors = reactive({
  username: '',
  password: ''
})

const validateForm = () => {
  errors.username = ''
  errors.password = ''

  if (!form.username.trim()) {
    errors.username = '用户名不能为空'
    return false
  }

  if (!form.password) {
    errors.password = '密码不能为空'
    return false
  }

  return true
}

const togglePassword = () => {
  showPassword.value = !showPassword.value
}

const switchLoginMethod = (method: 'password' | 'sms' | 'qr') => {
  loginMethod.value = method
  // 重置表单和错误状态
  resetForm()
  authStore.resetLoadingState()
  // 通知父组件登录方式变化
  emit('login-method-change', method)
}

const handleLogin = async () => {
  if (!validateForm()) return

  // 立即重置加载状态，确保按钮可以显示加载动画
  authStore.resetLoadingState()

  const success = await authStore.login({
    username: form.username,
    password: form.password
  })

  if (success) {
    console.log('LoginForm: Login successful')
    handleLoginSuccess()
  } else {
    // 登录失败，重置加载状态
    authStore.resetLoadingState()
  }
}

const handleLoginSuccess = () => {
  console.log('LoginForm: Login successful')
  props.onSuccess?.()
  resetForm()
}

const resetForm = () => {
  form.username = ''
  form.password = ''
  errors.username = ''
  errors.password = ''
}

// 暴露重置表单方法给父组件
defineExpose({
  resetForm
})

// 组件挂载时发送初始登录方式
onMounted(() => {
  emit('login-method-change', loginMethod.value)
})
</script>

<style scoped>
.login-form-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.login-method-switch {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #333;
}

.switch-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: all 0.2s ease;
  color: #888;
  border: 1px solid #444;
}

.switch-button:hover {
  background-color: rgba(255, 107, 53, 0.1);
  color: var(--p-primary-400);
  border-color: var(--p-primary-400);
  transform: scale(1.1);
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group input {
  width: 100%;
  box-sizing: border-box;
  background: #2a2a2a;
  border: 1px solid #444;
  color: #fff;
}

.form-group input::placeholder {
  color: #888;
}

.form-group input:focus {
  outline: none;
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
}

.form-group label {
  font-weight: 500;
  color: var(--p-primary-400);
  font-size: 14px;
}

.error-message {
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
}

.global-error {
  background: #2d1a1a;
  border: 1px solid #e74c3c;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
}

.form-actions {
  margin-top: 10px;
}

.login-button {
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
}

.form-footer {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #333;
}

.form-footer p {
  margin: 0;
  color: #ccc;
  font-size: 14px;
}

.form-footer a {
  color: var(--p-primary-400);
  text-decoration: none;
  font-weight: 500;
}

.form-footer a:hover {
  text-decoration: underline;
}

.password-input-container {
  position: relative;
  width: 100%;
}

.password-input-container input {
  width: 100%;
  padding-right: 45px; /* 为密码切换按钮留出空间 */
  box-sizing: border-box;
  background: #2a2a2a;
  border: 1px solid #444;
  color: #fff;
}

.password-input-container input::placeholder {
  color: #888;
}

.password-input-container input:focus {
  outline: none;
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 2px rgba(255, 107, 53, 0.2);
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  padding: 8px;
}

.password-toggle:hover {
  background-color: rgba(255, 107, 53, 0.1);
  border-radius: 4px;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .password-input-container input {
    padding-right: 40px; /* 在移动端稍微减少右边距 */
  }

  .password-toggle {
    right: 8px;
    padding: 6px;
  }
}
</style>
